import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { ShieldCheck, Plus, Save, Trash2, X, Lock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const PROTECTED = ["Proprietario", "Supervisore"];

export default function Ruoli() {
  const { user } = useAuth();
  const isOwner = user?.role === "Proprietario";
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id, name, percent}
  const [newRole, setNewRole] = useState({ name: "", percent: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/roles");
      setRoles(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento ruoli");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const addRole = async () => {
    const pct = parseFloat(newRole.percent);
    if (!newRole.name.trim()) { toast.error("Inserisci il nome del ruolo"); return; }
    if (Number.isNaN(pct) || pct < 0 || pct > 100) { toast.error("Percentuale non valida (0-100)"); return; }
    setBusy(true);
    try {
      await api.post("/roles", { name: newRole.name.trim(), percent: pct / 100 });
      setNewRole({ name: "", percent: "" });
      toast.success("Ruolo creato");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    const pct = parseFloat(editing.percent);
    if (!editing.name.trim()) { toast.error("Inserisci il nome del ruolo"); return; }
    if (Number.isNaN(pct) || pct < 0 || pct > 100) { toast.error("Percentuale non valida (0-100)"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/roles/${editing.id}`, { name: editing.name.trim(), percent: pct / 100 });
      setEditing(null);
      toast.success(`Ruolo aggiornato · ${data.recalculated_fatture || 0} fatture ricalcolate`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeRole = async (r) => {
    if (!window.confirm(`Eliminare il ruolo "${r.name}"?`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      toast.success("Ruolo eliminato");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!isOwner) {
    return (
      <div className="w-full">
        <PageHeader icon={ShieldCheck} title="Ruoli" subtitle="Gestione ruoli e percentuali." />
        <div className="ot-panel p-6 text-slate-400 text-sm" data-testid="ruoli-no-access">
          Solo il Proprietario può gestire i ruoli e le percentuali.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <PageHeader
        icon={ShieldCheck}
        title="Ruoli"
        subtitle="Crea, rinomina e modifica la percentuale di stipendio di ogni ruolo."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 ot-panel-elev p-4 ot-fade-up">
          <h2 className="ot-heading text-lg font-bold text-slate-100 mb-3">Ruoli Attuali</h2>
          {loading ? (
            <div className="text-slate-500 text-sm">Caricamento...</div>
          ) : (
            <div className="space-y-2" data-testid="roles-list">
              {roles.map((r) => {
                const isEdit = editing?.id === r.id;
                const locked = PROTECTED.includes(r.name);
                return (
                  <div key={r.id} className="ot-panel p-3" data-testid={`role-row-${r.id}`}>
                    {isEdit ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="ot-label block mb-1">NOME RUOLO</label>
                            <input
                              data-testid="edit-role-name"
                              className="ot-input"
                              disabled={locked}
                              value={editing.name}
                              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                            />
                            {locked && <div className="text-[0.65rem] text-slate-500 mt-1">Ruolo di sistema: nome non modificabile</div>}
                          </div>
                          <div>
                            <label className="ot-label block mb-1">PERCENTUALE (%)</label>
                            <input
                              data-testid="edit-role-percent"
                              type="number"
                              min="0"
                              max="100"
                              className="ot-input ot-mono"
                              value={editing.percent}
                              onChange={(e) => setEditing({ ...editing, percent: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button data-testid="save-role-btn" disabled={busy} onClick={saveEdit} className="ot-btn-primary flex items-center gap-2">
                            <Save className="w-4 h-4" /> Salva
                          </button>
                          <button data-testid="cancel-role-btn" onClick={() => setEditing(null)} className="ot-btn-ghost flex items-center gap-2">
                            <X className="w-4 h-4" /> Annulla
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        {locked ? <Lock className="w-4 h-4 text-amber-400/70 shrink-0" /> : <ShieldCheck className="w-4 h-4 text-blue-400/70 shrink-0" />}
                        <span className="text-slate-200 text-sm flex-1 min-w-[40%]">{r.name}</span>
                        <span className="ot-mono font-bold text-blue-400">{Math.round((r.percent || 0) * 100)}%</span>
                        <button
                          onClick={() => setEditing({ id: r.id, name: r.name, percent: String(Math.round((r.percent || 0) * 100)) })}
                          data-testid={`edit-role-${r.id}`}
                          className="px-2 py-1 rounded text-[0.65rem] ot-heading tracking-widest text-blue-400 border border-blue-400/30 hover:bg-blue-400/10 transition-colors"
                        >
                          MODIFICA
                        </button>
                        {!locked && (
                          <button onClick={() => removeRole(r)} data-testid={`delete-role-${r.id}`} className="p-1.5 rounded text-red-400 hover:bg-red-500/10 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-slate-500 text-xs mt-3 leading-relaxed">
            Modificando una percentuale, tutte le fatture di quel ruolo vengono ricalcolate con il nuovo valore.
          </p>
        </div>

        <div className="ot-panel-elev p-4 ot-fade-up" style={{ animationDelay: "80ms" }}>
          <h2 className="ot-heading text-lg font-bold text-slate-100 mb-3">Nuovo Ruolo</h2>
          <div className="space-y-3">
            <div>
              <label className="ot-label block mb-1">NOME RUOLO</label>
              <input
                data-testid="new-role-name"
                className="ot-input"
                placeholder="Es. Meccanico Senior"
                value={newRole.name}
                onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
              />
            </div>
            <div>
              <label className="ot-label block mb-1">PERCENTUALE (%)</label>
              <input
                data-testid="new-role-percent"
                type="number"
                min="0"
                max="100"
                className="ot-input ot-mono"
                placeholder="30"
                value={newRole.percent}
                onChange={(e) => setNewRole({ ...newRole, percent: e.target.value })}
              />
            </div>
            <button data-testid="add-role-btn" disabled={busy} onClick={addRole} className="ot-btn-primary w-full flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Crea Ruolo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
